import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

const variantConfig = {
  info: {
    icon: Info,
    wrapper: 'border-status-info/30 bg-status-info-subtle',
    iconColor: 'text-status-info',
  },
  success: {
    icon: CheckCircle2,
    wrapper: 'border-status-success/30 bg-status-success-subtle',
    iconColor: 'text-status-success',
  },
  warning: {
    icon: AlertTriangle,
    wrapper: 'border-status-warning/30 bg-status-warning-subtle',
    iconColor: 'text-status-warning',
  },
  danger: {
    icon: XCircle,
    wrapper: 'border-status-danger/30 bg-status-danger-subtle',
    iconColor: 'text-status-danger',
  },
} as const

export type AlertVariant = keyof typeof variantConfig

export interface AlertProps {
  readonly variant?: AlertVariant
  readonly title: string
  readonly children?: ReactNode
  readonly className?: string
}

export function Alert({ variant = 'info', title, children, className }: AlertProps) {
  const config = variantConfig[variant]
  const Icon = config.icon

  return (
    <div
      role={variant === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex gap-3 rounded-md border p-4 text-sm text-foreground-primary',
        config.wrapper,
        className,
      )}
    >
      <Icon aria-hidden="true" className={cn('mt-0.5 h-5 w-5 shrink-0', config.iconColor)} />
      <div className="flex flex-col gap-1">
        <p className="font-medium">{title}</p>
        {children !== undefined ? (
          <div className="text-foreground-secondary">{children}</div>
        ) : null}
      </div>
    </div>
  )
}
